import {
    Chip,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components';
import { COLOR } from '@/theme/colors';

import { AccountingProviderLog, Props } from './types';

const headerFontStyle: SxProps = {
    color: COLOR.paleSky,
    fontWeight: 700,
    fontSize: 13,
};

const getTypeChipColor = (
    type: AccountingProviderLog['type'],
): 'error' | 'warning' | 'info' => {
    switch (type) {
        case 'error':
            return 'error';
        case 'warning':
            return 'warning';
        case 'info':
            return 'info';
        default:
            return 'info';
    }
};

function AccountingProviderLogTable({ logs, emptyStateMessage }: Props) {
    const { t } = useTranslation();

    return (
        <TableContainer>
            {!logs.length && emptyStateMessage ? (
                <EmptyState
                    testID="AccountingProviderLogTable-EmptyState"
                    message={emptyStateMessage}
                />
            ) : null}
            {logs.length ? (
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography sx={headerFontStyle}>
                                    {t('Invoicing:events_logs:columns:date')}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography sx={headerFontStyle}>
                                    {t('Invoicing:events_logs:columns:message')}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography sx={headerFontStyle}>
                                    {t('Invoicing:events_logs:columns:type')}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography sx={headerFontStyle}>
                                    {t(
                                        'Invoicing:events_logs:columns:provider',
                                    )}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody data-test-id="AccountingProviderLogTable-TableBody">
                        {logs.map((log) => (
                            <TableRow key={log.id}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Typography fontSize={14}>
                                        {log.date}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography fontSize={14}>
                                        {log.message}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(
                                            `Invoicing:events_logs:types:${log.type}`,
                                        )}
                                        color={getTypeChipColor(log.type)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography fontSize={14}>
                                        {t(
                                            `Invoicing:events_logs:providers:${log.provider}`,
                                        )}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : null}
        </TableContainer>
    );
}

export default memo(AccountingProviderLogTable);
